import React, { Suspense, useState } from 'react';
import { PageFallback } from '../components/PageFallback';

/**
 * A code-split route component that can be loaded before the first render.
 *
 * Plain React.lazy suspends on its first render no matter what. On a page
 * react-snap prerendered that is a hydration mismatch -- the static HTML has no
 * Suspense markers -- so React throws #418/#423 and re-renders the whole root on
 * the client, swapping the prerendered page for a spinner and back. src/index.js
 * calls `preloadRoute()` (src/Routes.js), which awaits this component's
 * `preload()` for the current URL, so by the time hydrateRoot runs the module is
 * already here and the page renders synchronously, exactly as react-snap saw it.
 *
 * The Suspense boundary lives here, on the not-yet-loaded branch only, rather
 * than around the route: a <Suspense> on the hydration path is itself a
 * mismatch, because react-snap's HTML carries none of React's boundary markers.
 *
 * Which branch an instance uses is fixed when it mounts. Switching from the lazy
 * element to the loaded one on a later render would be a different element type,
 * which remounts the page and throws its state away.
 */
const RELOAD_KEY = 'pev_chunk_reload_at';
const RELOAD_WINDOW_MS = 30000;

/** webpack's error when a chunk file is gone, e.g. a tab opened before a deploy. */
export const isChunkLoadError = (error) =>
  !!error && (error.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/.test(error.message || ''));

/**
 * A tab opened before a deploy still asks for the old hashed chunk names, which
 * the new version no longer serves. React.lazy caches that rejection for good,
 * so the route would stay broken until a manual reload: reload once instead,
 * which fetches the new index and the new chunk names. Returns true when it
 * reloaded. A second failure inside the window is not a stale tab -- the chunk
 * really is missing -- so it falls through to the ErrorBoundary rather than
 * reloading in a loop.
 */
export const recoverFromChunkError = (error, {
  storage,
  reload = () => window.location.reload(),
  now = Date.now(),
} = {}) => {
  if (!isChunkLoadError(error)) return false;
  try {
    // Read inside the try: in some private modes touching sessionStorage throws.
    const store = storage || window.sessionStorage;
    const last = Number(store.getItem(RELOAD_KEY));
    if (last && now - last < RELOAD_WINDOW_MS) return false;
    store.setItem(RELOAD_KEY, String(now));
  } catch (e) {
    return false; // no storage, no loop guard: let the error show
  }
  reload();
  return true;
};

export const lazyPage = (load) => {
  let Loaded = null;
  let pending = null;

  const preload = () => {
    if (!pending) {
      pending = load().then((module) => {
        Loaded = module.default;
        return Loaded;
      });
      // A failed chunk load must stay retryable, as React.lazy's own would not.
      pending.catch(() => { pending = null; });
    }
    return pending;
  };

  const Lazy = React.lazy(() => preload()
    .then((Component) => ({ default: Component }))
    // Reloading: never settle, so nothing renders the error in the meantime.
    .catch((error) => (recoverFromChunkError(error) ? new Promise(() => {}) : Promise.reject(error))));

  const Page = (props) => {
    const [Ready] = useState(() => Loaded);
    return Ready
      ? <Ready {...props} />
      : <Suspense fallback={<PageFallback />}><Lazy {...props} /></Suspense>;
  };
  Page.preload = preload;
  return Page;
};

export default lazyPage;
